<template>
    <div class="d-flex flex-column min-vh-100">
        <!-- Both bars stick together as one unit. The sticky element must be this wrapper: its parent
             is the full-height column, so it stays stuck for the whole page. -->
        <div class="site-header sticky-top" ref="siteHeader">
            <header class="navbar navbar-expand-md d-print-none flex-grow-0 site-topbar" ref="topbar">
                <div class="container-xl">
                    <!-- No category menu when ordering is closed, so nothing to toggle. -->
                    <button v-if="!shut" class="navbar-toggler collapsed" type="button" data-bs-toggle="collapse"
                            data-bs-target="#navbar-menu" aria-controls="navbar-menu" aria-expanded="false"
                            aria-label="Toggle navigation">
                        <span class="navbar-toggler-icon"></span>
                    </button>
                    <a class="navbar-brand d-flex align-items-center gap-2" href="/" aria-label="Larkhall Butchers Christmas Orders, home page">
                        <img height="50" src="/larkhall.png" alt="Larkhall Butchers"><span class="d-none d-sm-block">Christmas Orders</span>
                    </a>
                  <div class="nav-item" v-if="shut">
                    <a href="/my-order" class="btn btn-on-dark" >
                      View My Order
                    </a>
                  </div>
                    <div class="nav-item header-basket" v-if="!shut">
                        <a href="/basket/" class="nav-link d-flex lh-1 text-reset p-0" :aria-label="basketLabel">
                            <span class="avatar">
                                <IconShoppingCart></IconShoppingCart>
                                <span v-if="basketCount > 0" class="badge badge-notification badge-pill basket-count" data-testid="basket-count" aria-hidden="true">{{ basketCount }}</span>
                            </span>
                        </a>
                    </div>
                </div>
            </header>
            <header class="navbar-expand-md" v-if="!shut">
                <div class="collapse navbar-collapse" id="navbar-menu">
                    <div class="navbar">
                        <div class="container-xl">
                            <div class="row flex-fill align-items-center">
                                <div class="col menu-col">
                                    <MenuItems></MenuItems>
                                </div>
                                <div class="col-md-auto header-links">
                                    <ul class="navbar-nav">
                                        <li :class="['nav-item', { active: $route.path === '/FAQ' }]">
                                            <a class="nav-link" href="/FAQ">
                                                <span class="nav-link-title">FAQ</span>
                                            </a>
                                        </li>
                                        <li :class="['nav-item', { active: $route.path === '/my-order' }]">
                                            <a class="nav-link" href="/my-order">
                                                <span class="nav-link-title">My Order</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </div>

        <main class="flex-grow-1">
            <slot></slot>
        </main>

        <footer class="footer  bg-primary text-primary-fg d-print-none">
            <div class="container-xl">
                <div class="row text-center align-items-center flex-row-reverse">
                    <div class="col-lg-auto ms-lg-auto">
                        <ul class="list-inline list-inline-dots mb-0">
                            <li class="list-inline-item"><a href="https://larkhallbutchers.co.uk/privacy-policy" target="_blank"
                                                            class="link-secondary text-primary-fg" rel="noopener">Privacy Policy</a></li>
                            <li class="list-inline-item"><a href="/FAQ" target="_blank"
                                                            class="link-secondary text-primary-fg" rel="noopener">FAQ</a></li>
                            <li class="list-inline-item"><a href="/my-order"
                                                            class="link-secondary text-primary-fg" rel="noopener">My Order</a></li>
                        </ul>
                    </div>
                    <div class="col-12 col-lg-auto mt-3 mt-lg-0">
                        <ul class="list-inline list-inline-dots mb-0">
                            <li class="list-inline-item">
                                Copyright © 2026
                                Larkhall Butchers
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </footer>
    </div>
</template>
<script>
import MenuItems from "@/components/MenuItems.vue"
import {IconShoppingCart} from "@tabler/icons-vue"
import api from "@/services/api"

export default {
    name: "BasePage",
    components: {MenuItems, IconShoppingCart},
    data() {
      return {
        // Same build setting as the routes (router/index.js): closed unless VUE_APP_STORE_OPEN=true
        shut: process.env.VUE_APP_STORE_OPEN !== 'true',
        basketCount: 0
      }
    },
    computed: {
      basketLabel() {
        if (this.basketCount === 0) return 'Basket';
        return `Basket, ${this.basketCount} ${this.basketCount === 1 ? 'item' : 'items'}`;
      }
    },
    methods: {
      // Number of lines in the basket, from the same route the basket page reads. A page that
      // already knows the count passes it in the event so no second request is made.
      async refreshBasketCount(event) {
        if (this.shut) return;
        if (event && event.detail && Number.isFinite(event.detail.count)) {
          this.basketCount = event.detail.count;
          return;
        }
        const basketId = localStorage.getItem('basketId2026');
        if (!basketId) {
          this.basketCount = 0;
          return;
        }
        try {
          const res = await api.get('/api/basket/checkout/' + basketId);
          this.basketCount = Array.isArray(res.data) ? res.data.length : 0;
        } catch (e) {
          // The count is a convenience: leave it as it was when the request fails.
        }
      },
      // The sticky header's real height (it changes with the window width and the phone menu),
      // published as CSS variables: scroll-padding-top keeps anchors and scrollIntoView() targets
      // clear of the header, and the phone menu's max-height leaves room for the top bar.
      measureHeader() {
        const root = document.documentElement.style;
        if (this.$refs.siteHeader) root.setProperty('--site-header-height', this.$refs.siteHeader.offsetHeight + 'px');
        if (this.$refs.topbar) root.setProperty('--site-topbar-height', this.$refs.topbar.offsetHeight + 'px');
      }
    },
    mounted() {
      window.addEventListener('basket-changed', this.refreshBasketCount);
      this.refreshBasketCount();
      this.measureHeader();
      if (typeof ResizeObserver !== 'undefined') {
        this.headerObserver = new ResizeObserver(() => this.measureHeader());
        this.headerObserver.observe(this.$refs.siteHeader);
      }
    },
    beforeUnmount() {
      window.removeEventListener('basket-changed', this.refreshBasketCount);
      if (this.headerObserver) this.headerObserver.disconnect();
    }
}
</script>

<style>
/* Unscoped: these apply to the page, not to this component. Keeps in-page anchors and
   scrollIntoView() targets (the checkout's first invalid field) clear of the sticky header. */
html {
  scroll-padding-top: calc(var(--site-header-height, 0px) + 0.75rem);
}
@media print {
  html {
    scroll-padding-top: 0;
  }
}
</style>

<style scoped>
/* The top bar colour, defined once. */
.site-header {
  --site-topbar-bg: #4A5653;
  --site-topbar-fg: #ffffff;
  box-shadow: 0 10px 30px -20px rgba(16, 24, 40, 0.3);
}
@media print {
  .site-header {
    position: static;
    box-shadow: none;
  }
}

.site-topbar {
  --tblr-navbar-bg: var(--site-topbar-bg);
  --tblr-navbar-color: var(--site-topbar-fg);
  --tblr-navbar-brand-color: var(--site-topbar-fg);
  --tblr-navbar-brand-hover-color: var(--site-topbar-fg);
  --tblr-navbar-border-color: transparent;
  background: var(--site-topbar-bg);
  color: var(--site-topbar-fg);
}
.site-topbar .navbar-brand,
.site-topbar .navbar-brand:hover {
  color: var(--site-topbar-fg);
}
/* Tabler draws the toggler's bars in currentColor, so this makes them light. */
.site-topbar .navbar-toggler {
  color: var(--site-topbar-fg);
}
.site-topbar a:focus-visible,
.site-topbar .navbar-toggler:focus-visible {
  outline: 2px solid var(--site-topbar-fg);
  outline-offset: 2px;
}
/* The basket: light icon on a faint light tile, light badge with dark text. */
.site-topbar .avatar {
  --tblr-avatar-bg: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.12);
  color: var(--site-topbar-fg);
}
.site-topbar .basket-count {
  background: var(--site-topbar-fg);
  color: var(--site-topbar-bg);
}
/* Closed season "View My Order": outline button in light colours. */
.btn-on-dark {
  color: var(--site-topbar-fg);
  background: transparent;
  border-color: rgba(255, 255, 255, 0.75);
}
.btn-on-dark:hover,
.btn-on-dark:focus-visible {
  color: var(--site-topbar-bg);
  background: var(--site-topbar-fg);
  border-color: var(--site-topbar-fg);
}

/* FAQ and My Order: on the right of the category bar on desktop; on phones, at the end of the
   collapsed menu below a divider. */
@media (min-width: 768px) {
  .header-links {
    margin-left: auto;
  }
}
@media (max-width: 767.98px) {
  .header-links {
    border-top: 1px solid var(--tblr-border-color);
    margin-top: 0.25rem;
    padding-top: 0.25rem;
  }
  /* Every group hidden from the menu (WP-38): no empty column and no divider above the links. */
  .menu-col:empty {
    display: none;
  }
  .menu-col:empty + .header-links {
    border-top: 0;
    margin-top: 0;
    padding-top: 0;
  }
  /* The open phone menu scrolls inside itself when it is taller than the screen,
     with the top bar still showing above it. */
  #navbar-menu {
    max-height: calc(100vh - var(--site-topbar-height, 4.625rem));
    max-height: calc(100dvh - var(--site-topbar-height, 4.625rem));
    overflow-y: auto;
    overscroll-behavior: contain;
  }
}

/* The count badge hangs 0.5rem past the icon. Between 768px and 992px the header has only 0.5rem
   of side padding, which put the badge on the very edge of the window; this keeps it clear. */
.header-basket {
  flex: none;
  margin-right: 0.75rem;
}
.avatar {
  position: relative;
  overflow: visible;
}
.basket-count {
  position: absolute;
  top: -0.375rem;
  right: -0.5rem;
  bottom: auto;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.3rem;
  border-radius: 999px;
  font-size: 0.75rem;
  line-height: 1.25rem;
  transform: none;
  box-shadow: none;
}
</style>